/**
 * History (ADR 0109) for the console: the page's date range in the URL, and the rollup series and
 * ended sessions for it from /api/history (or demo data in ?mock=1). Refetched every minute while
 * the range runs to now, so today stays live.
 */
import { HISTORY_METRICS } from '@orgus/protocol';
import type { HistoryBucketSize, HistoryMetric, HistorySeriesResult, HistorySession, HistorySessionsResult } from '@orgus/protocol';
import { parseDateRange, rangeBucket, toUiError, useNow, writeDateRange } from '@orgus/ui';
import type { DateRange, DateRangePreset, UiError } from '@orgus/ui';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { api } from '../../lib/api.ts';
import { useOrgus } from '../../lib/store.ts';
import { mockSeries, mockSessions } from './mock.ts';

/** The page's range, from and to the URL (`?range=`, `?from&to` for custom). */
export function useDateRange(fallback: Exclude<DateRangePreset, 'custom'> = '7d'): { range: DateRange; setRange(r: DateRange): void } {
  const [params, setParams] = useSearchParams();
  // A preset's end is now: a minute clock keeps it current without refetching on every render.
  const now = useNow(60_000);
  const key = `${params.get('range') ?? ''}|${params.get('from') ?? ''}|${params.get('to') ?? ''}`;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const range = useMemo(() => parseDateRange(params, now, fallback), [key, now, fallback]);
  return { range, setRange: (r) => setParams((p) => writeDateRange(r, p, fallback), { replace: true }) };
}

export interface Loaded<T> {
  data: T | null;
  loading: boolean;
  /** A newer answer is on its way while the last one shows. */
  refreshing: boolean;
  error: UiError | null;
  retry(): void;
  /** Updates as it happens (a range that runs to now, on a live connection). */
  live: boolean;
}

function useLoad<T>(key: string, load: (signal: AbortSignal) => Promise<T>, follow: boolean): Loaded<T> {
  const mode = useOrgus((s) => s.mode);
  const connected = useOrgus((s) => s.connection === 'live');
  const [state, setState] = useState<{ key: string; data: T | null; error: UiError | null; loading: boolean }>({ key: '', data: null, error: null, loading: true });
  const [nonce, setNonce] = useState(0);
  const tick = useNow(follow ? 60_000 : 3_600_000);
  useEffect(() => {
    const ctrl = new AbortController();
    setState((s) => (s.key === key ? { ...s, loading: !s.data } : { key, data: null, error: null, loading: true }));
    load(ctrl.signal)
      .then((data) => setState({ key, data, error: null, loading: false }))
      .catch((e: unknown) => {
        if (!ctrl.signal.aborted) setState((s) => ({ ...s, key, error: toUiError(e, { what: 'The history did not load.' }), loading: false }));
      });
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, nonce, follow ? tick : 0, mode]);
  const mine = state.key === key;
  return {
    data: mine ? state.data : null,
    loading: !mine || (state.loading && !state.data),
    refreshing: mine && state.loading && !!state.data,
    error: mine ? state.error : null,
    retry: () => setNonce((n) => n + 1),
    live: follow && (connected || mode === 'mock'),
  };
}

export interface SeriesQuery {
  metrics: readonly HistoryMetric[];
  groupBy?: string | null;
  bucket?: HistoryBucketSize;
}

/** Rollup series for the range. */
export function useHistorySeries(range: DateRange, q: SeriesQuery): Loaded<HistorySeriesResult> {
  const mode = useOrgus((s) => s.mode);
  const bucket = q.bucket ?? rangeBucket(range);
  const groupBy = q.groupBy ?? null;
  const key = `${mode}|${range.from}|${range.preset === 'custom' ? range.to : 'now'}|${q.metrics.join(',')}|${groupBy ?? ''}|${bucket}`;
  return useLoad(
    key,
    async (signal) => {
      const to = range.preset === 'custom' ? range.to : Date.now();
      if (mode === 'mock') return mockSeries(range.from, to, q.metrics, groupBy, bucket);
      const p = new URLSearchParams({ from: String(range.from), to: String(to), metrics: q.metrics.join(','), bucket, tz: String(-new Date(to).getTimezoneOffset()) });
      if (groupBy) p.set('groupBy', groupBy);
      return api<HistorySeriesResult>(`/api/history/series?${p.toString()}`, { signal });
    },
    range.preset !== 'custom',
  );
}

/** Ended sessions in the range (the first page), filtered by text. */
export function useHistorySessions(range: DateRange, q = ''): Loaded<HistorySessionsResult> {
  const mode = useOrgus((s) => s.mode);
  const key = `${mode}|${range.from}|${range.preset === 'custom' ? range.to : 'now'}|${q}`;
  return useLoad(
    key,
    async (signal) => {
      const to = range.preset === 'custom' ? range.to : Date.now();
      if (mode === 'mock') return mockSessions(range.from, to, q);
      const p = new URLSearchParams({ from: String(range.from), to: String(to) });
      if (q) p.set('q', q);
      return api<HistorySessionsResult>(`/api/history/sessions?${p.toString()}`, { signal });
    },
    range.preset !== 'custom',
  );
}

/** A series' total over the range. */
export const total = (r: HistorySeriesResult | null, metric: HistoryMetric, group?: string): number =>
  (r?.series ?? []).filter((s) => s.metric === metric && (group == null || s.group === group)).reduce((a, s) => a + s.values.reduce((x, y) => x + y, 0), 0);

/** Can this metric be grouped by that dimension? */
export const groupable = (metric: HistoryMetric, dim: string): boolean => (HISTORY_METRICS[metric] as readonly string[]).includes(dim);

export type { HistorySession };
